<template>
  <div v-if="!selectedCategoryLevel" class="emh-filters-content" data-test-id="emh-filters-content--first-level">
    <slot name="head"></slot>
    <emh-side-filters-categories
      :filter-categories="filteredSideFilters"
      :is-loading="isLoading"
      data-test-id="emh-filters-content__filters-categories"
      @select-category="onSelectSubCategory"
    ></emh-side-filters-categories>
  </div>
  <div v-else class="emh-filters-content" data-test-id="emh-filters-content--second-level">
    <emh-side-filters-category-content-template
      :headline="selectedCategorySettings.name"
      :icon="selectedCategorySettings.icon"
      :is-loading="isLoading"
      data-test-id="emh-filters-content__category-filters-content-template"
      @navigate-back="onSelectSubCategory()"
    >
      <testing-generic-wrapper storage-key="ClearSectionButton">
        <template #targetB>
          <button
            v-if="isAnyCategorySettingsSelected"
            :disabled="isLoading"
            class="emh-filters-content__reset-category-filters emh-filters-content__category-reset-button wb-button wb-button--secondary wb-button--small"
            data-test-id="emh-filters-content__category-reset-button"
            @click="onClearAllCategories"
          >
            <wb-icon
              :name.attr="'close'"
              aria-hidden="true"
              class="emh-filters-content__category-reset-button-icon wb-button__icon"
              flip-rtl
            ></wb-icon>
            {{ $t('dcp.assortment.cars.filtersSearch.filters.reset') }}
            <emh-selected-filters-counter
              :filters="selectedCategorySettings.filters"
              class="emh-filters-content__counter"
              hide-zero-counter
              mode="tertiary"
            />
          </button>
        </template>
        <template #control>
          <button
            v-if="isAnyFilterSelected"
            :disabled="isLoading"
            class="emh-filters-content__category-reset-button wb-button wb-button--tertiary wb-button--small"
            data-test-id="emh-filters-content__category-reset-button"
            @click="onClearAllFilters"
          >
            <wb-icon :name.attr="'close'" aria-hidden="true" class="wb-button__icon" flip-rtl></wb-icon>
            {{ $t('dcp.assortment.cars.filtersSearch.filters.reset') }}
            ({{ selectedFiltersCount }})
          </button>
        </template>
      </testing-generic-wrapper>

      <component
        :is="selectedCategoryLevelComponentName"
        :filter-category="selectedCategorySettings"
        :is-loading="isLoading"
        data-test-id="emh-filters-content__category-filters-content"
        @change="onChangeFilter"
        @clear-category="onClearCategory"
      />
    </emh-side-filters-category-content-template>
  </div>
</template>

<script lang="ts">
import { IProductFilterGroup } from '@dh-io-emh/shared/src/models/product-filter/product-filter-base.model';
import { IProductFilterType } from '@dh-io-emh/shared/src/models/product-filter/product-filter.model';
import { computed, defineComponent, PropType, toRefs } from 'vue';
import EmhSideFiltersCampaignCategory from '../sideFiltersCategories/sideFiltersCampaignCategory/SideFiltersCampaignCategory.vue';
import EmhSideFiltersCategories from '../sideFiltersCategories/SideFiltersCategories.vue';
import EmhSideFiltersCategoryContentTemplate from '../sideFiltersCategories/sideFiltersCategoryContentTemplate/SideFiltersCategoryContentTemplate.vue';
import EmhSideFiltersColorCategory from '../sideFiltersCategories/sideFiltersColorCategory/SideFiltersColorCategory.vue';
import EmhSideFiltersEngineCategory from '../sideFiltersCategories/sideFiltersEngineCategory/SideFiltersEngineCategory.vue';
import EmhSideFiltersEquipmentCategory from '../sideFiltersCategories/sideFiltersEquipmentCategory/SideFiltersEquipmentCategory.vue';
import EmhSideFiltersLocationsCategory from '../sideFiltersCategories/sideFiltersLocationsCategory/SideFiltersLocationsCategory.vue';
import EmhSideFiltersPriceCategory from '../sideFiltersCategories/sideFiltersPriceCategory/SideFiltersPriceCategory.vue';
import EmhSideFiltersStockTypeCategory from '../sideFiltersCategories/sideFiltersStockTypeCategory/SideFiltersStockTypeCategory.vue';
import EmhSideFiltersTransportUsageCategory from '../sideFiltersCategories/sideFiltersTransportUsageCategory/SideFiltersTransportUsageCategory.vue';
import EmhSelectedFiltersCounter from '../selectedFiltersCounter/SelectedFiltersCounter.vue';
import TestingGenericWrapper from '@dh-io-emh/shared/src/components/testingGenericWrapper/TestingGenericWrapper.vue';
import { useSideFiltersContent } from './side-filters-content.composable';
import EmhSideFiltersUpholsteryCategory from '../sideFiltersCategories/sideFiltersUpholsteryCategory/SideFiltersUpholsteryCategory.vue';
import EmhSideFiltersVehicleCategory from '../sideFiltersCategories/sideFiltersVehicleCategory/SideFiltersVehicleCategory.vue';

export default defineComponent({
  name: 'EmhSideFiltersContent',
  components: {
    EmhSideFiltersTransportUsageCategory,
    EmhSideFiltersLocationsCategory,
    EmhSideFiltersCategoryContentTemplate,
    EmhSideFiltersCampaignCategory,
    EmhSideFiltersCategories,
    EmhSideFiltersColorCategory,
    EmhSideFiltersEquipmentCategory,
    EmhSideFiltersEngineCategory,
    EmhSideFiltersPriceCategory,
    EmhSideFiltersStockTypeCategory,
    EmhSideFiltersVehicleCategory,
    EmhSideFiltersUpholsteryCategory,
    EmhSelectedFiltersCounter,
    TestingGenericWrapper,
  },

  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    filterCategories: {
      type: Array as PropType<IProductFilterGroup[]>,
      default: () => [],
    },
    selectedFilters: {
      type: Array as PropType<IProductFilterType[]>,
      default: () => [],
    },
    selectedCategoryLevel: {
      type: String,
      default: '',
    },
  },

  emits: ['change-filter', 'clear-all-filters', 'clear-category', 'select-subcategory'],

  setup(props, { emit }) {
    const { filterCategories, selectedFilters, selectedCategoryLevel, isLoading } = toRefs(props);

    const {
      onChangeFilter,
      onClearAllFilters,
      onClearCategory,
      getCategoryComponentName,
      filteredSideFilters,
      isAnyFilterSelected,
      selectedFiltersCount,
    } = useSideFiltersContent(filterCategories, selectedFilters, emit);

    const isAnyCategorySettingsSelected = computed(() =>
      selectedCategorySettings.value?.filters.some((filter) => filter.isSelected()),
    );

    const selectedCategoryLevelComponentName = computed(() =>
      getCategoryComponentName(selectedCategoryLevel.value || ''),
    );

    const selectedCategorySettings = computed(() => {
      const selectedFilterCategories = filterCategories.value.find(({ code }) => code === selectedCategoryLevel.value);

      selectedFilterCategories?.filters.forEach((filter) => {
        filter.options?.forEach((option) => {
          option.disabled = isLoading.value;
        });
      });

      return selectedFilterCategories as IProductFilterGroup;
    });

    const onSelectSubCategory = (category?: string) => {
      emit('select-subcategory', category);
    };

    const onClearAllCategories = () => {
      onClearCategory(selectedCategorySettings.value?.filters.map((filter) => filter.code) || []);
    };

    return {
      filteredSideFilters,
      onChangeFilter,
      onClearAllFilters,
      onClearCategory,
      onClearAllCategories,
      onSelectSubCategory,
      isAnyCategorySettingsSelected,
      selectedCategoryLevelComponentName,
      selectedCategorySettings,
      isAnyFilterSelected,
      selectedFiltersCount,
    };
  },
});
</script>

<style lang="scss" scoped>
.emh-filters-content {
  @include wb-spacing('margin', 'xs 0 l');
  color: var(--wb-grey-20);

  &__category-reset-button {
    @include wb-spacing('margin-bottom', 's');
    @include breakpoint-to(mq4) {
      display: none;
    }
  }

  &__category-reset-button-icon {
    height: 8px;
    width: 8px;
  }

  &__counter {
    @include wb-spacing('margin-left', 'xxs');
  }
}

.emh-ui-checkbox {
  @include wb-spacing('margin-top', 'xs');
  @include wb-spacing('margin-bottom', 'm');
}
</style>
